import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField,
  Typography,
  Button,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import useSWR from 'swr';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useSelector } from "react-redux";
import { exportStyledExcel, pdfTableTheme, EXPORT_COLORS } from '../../utils/exportHelpers';
import { apiHost } from '../../utils/apiHost';

// Fungsi untuk mengambil data dari API
const fetcher = (url) => axios.get(url, { withCredentials: true }).then((res) => res.data);

// Fungsi untuk mendapatkan URL dasar API
const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  // Menggunakan window.location.hostname untuk menghindari masalah jika REACT_APP_URL tidak ada atau salah
  const baseUrl = apiHost() || window.location.hostname;
  return `${protocol}://${baseUrl.replace(/^https?:\/\//, '')}`;
};

// Fungsi utilitas untuk memformat tanggal ke YYYY-MM-DD
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Fungsi utilitas untuk mendapatkan tanggal awal dan akhir bulan
const getMonthStartAndEndDate = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    startDate: formatDate(firstDay),
    endDate: formatDate(lastDay)
  };
};

export const Komprehensif = () => {
  const { user } = useSelector((state) => state.auth);
  
  // Menggunakan tanggal awal dan akhir bulan berjalan sebagai nilai default
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getMonthStartAndEndDate();
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [selectedCabang, setSelectedCabang] = useState('');

  // Menggunakan useEffect untuk mengatur tanggal default saat komponen dimuat
  useEffect(() => {
    const { startDate, endDate } = getMonthStartAndEndDate();
    setStartDate(startDate);
    setEndDate(endDate);
  }, []);

  const { data: cabangList } = useSWR(
    user?.role === 'superadmin' ? `${getApiBaseUrl()}/cabang` : null,
    fetcher
  );

  const apiUrl = `${getApiBaseUrl()}/komprehensif?startDate=${startDate}&endDate=${endDate}${selectedCabang ? `&cabangUuid=${selectedCabang}` : ''}`;
  const { data, error } = useSWR(user ? apiUrl : null, fetcher);

  if (!user) {
    return <Typography color="error">Silakan login untuk melihat data.</Typography>;
  }

  if (error) return <Typography color="error">Gagal memuat data.</Typography>;
  if (!data) return <CircularProgress />;

  const getProductPrice = (productName) => {
    const product = data.detailedData.find(item => item.productName === productName);
    return product ? product.price : 0;
  };

  const getBranchName = (item) =>
    (item.stockByBranch || []).map((b) => b.branchName).filter(Boolean).join(', ') || '-';

  // Superadmin yang melihat "Semua Cabang" perlu kolom Cabang & urutan per cabang
  const showBranchColumn = user.role === 'superadmin' && !selectedCabang;

  const detailedDataSorted = showBranchColumn
    ? [...data.detailedData].sort((a, b) => {
        if (a.category !== b.category) return (a.category || '').localeCompare(b.category || '');
        const branchA = getBranchName(a);
        const branchB = getBranchName(b);
        if (branchA !== branchB) return branchA.localeCompare(branchB);
        return (a.productName || '').localeCompare(b.productName || '');
      })
    : data.detailedData;

  const handleExportPDF = () => {
    const doc = new jsPDF();
    let yPos = 15;

    // Judul
    doc.setFontSize(18);
    doc.text('Laporan Rekap Komprehensif', 14, yPos);
    yPos += 10;
    
    // Ringkasan
    doc.setFontSize(12);
    doc.text(`Periode: ${startDate} sampai ${endDate}`, 14, yPos);
    yPos += 10;
    doc.text(`Total Produk: ${data.summary.totalProducts}`, 14, yPos);
    yPos += 5;
    doc.text(`Total Kategori: ${data.summary.totalCategories}`, 14, yPos);
    yPos += 5;
    doc.text(`Total Stok: ${data.summary.totalStock}`, 14, yPos);
    yPos += 5;
    doc.text(`Total Penjualan: Rp ${data.summary.totalSalesAmount.toLocaleString()}`, 14, yPos);
    yPos += 15;

    // Produk Terlaris
    if (data.summary.topSellingProducts && data.summary.topSellingProducts.length > 0) {
      doc.text('Produk Terlaris (Top 5)', 14, yPos);
      yPos += 5;
      doc.autoTable({
        startY: yPos,
        head: [['Nama Produk', 'Kategori', 'Harga', 'Jumlah Terjual', 'Total Penjualan']],
        body: data.summary.topSellingProducts.map((item) => [
          item.name,
          item.category,
          `Rp ${getProductPrice(item.name).toLocaleString()}`,
          item.salesQuantity,
          `Rp ${item.salesAmount.toLocaleString()}`
        ]),
        ...pdfTableTheme,
      });
      yPos = doc.autoTable.previous.finalY + 10;
    }

    // Performa Cabang (khusus Superadmin)
    if (user.role === "superadmin" && data.summary.branchPerformance && data.summary.branchPerformance.length > 0) {
        doc.addPage();
        yPos = 15;
        doc.setFontSize(18);
        doc.text('Laporan Rekap Komprehensif', 14, yPos);
        yPos += 10;
        doc.setFontSize(12);
        doc.text('Performa Cabang', 14, yPos);
        yPos += 5;
        doc.autoTable({
            startY: yPos,
            head: [['Nama Cabang', 'Jumlah Transaksi', 'Total Penjualan']],
            body: data.summary.branchPerformance.map((item) => [
                item.branchName,
                item.transactionCount,
                `Rp ${item.totalSales.toLocaleString()}`
            ]),
            ...pdfTableTheme,
        });
        yPos = doc.autoTable.previous.finalY + 10;
    }
    
    // Data Detail Produk (dikelompokkan per kategori, urut cabang dulu jika superadmin lihat semua cabang)
    if (data.detailedData && data.detailedData.length > 0) {
      doc.addPage();
      yPos = 15;
      doc.setFontSize(18);
      doc.text('Laporan Rekap Komprehensif', 14, yPos);
      yPos += 10;
      doc.setFontSize(12);
      doc.text('Data Detail Produk', 14, yPos);
      yPos += 5;

      const subtotalStyle = { fontStyle: 'bold', fillColor: EXPORT_COLORS.subtotalFillPdf, halign: 'center' };
      const grandTotalStyle = { fontStyle: 'bold', fillColor: EXPORT_COLORS.grandTotalFillPdf, halign: 'center' };
      const labelSpan = showBranchColumn ? 3 : 2;

      const body = [];
      let grandQty = 0;
      let grandTotal = 0;

      const pushCategoryRows = (items, category) => {
        let catQty = 0;
        let catTotal = 0;
        items.forEach((item) => {
          const qty = item.sales?.quantity || 0;
          const total = item.sales?.amount || 0;
          catQty += qty;
          catTotal += total;
          const row = [item.productName, category];
          if (showBranchColumn) row.push(getBranchName(item));
          row.push(qty, `Rp ${total.toLocaleString()}`);
          body.push(row);
        });
        body.push([
          { content: `Total Kategori ${category}`, colSpan: labelSpan, styles: subtotalStyle },
          { content: catQty, styles: subtotalStyle },
          { content: `Rp ${catTotal.toLocaleString()}`, styles: subtotalStyle },
        ]);
        grandQty += catQty;
        grandTotal += catTotal;
      };

      const grouped = {};
      detailedDataSorted.forEach((item) => {
        if (!grouped[item.category]) grouped[item.category] = [];
        grouped[item.category].push(item);
      });
      Object.entries(grouped).forEach(([category, items]) => {
        pushCategoryRows(items, category);
      });

      body.push([
        { content: 'GRAND TOTAL', colSpan: labelSpan, styles: grandTotalStyle },
        { content: grandQty, styles: grandTotalStyle },
        { content: `Rp ${grandTotal.toLocaleString()}`, styles: grandTotalStyle },
      ]);

      doc.autoTable({
        startY: yPos,
        head: showBranchColumn
          ? [['Nama Produk', 'Kategori', 'Cabang', 'Jumlah Terjual', 'Total Penjualan']]
          : [['Nama Produk', 'Kategori', 'Jumlah Terjual', 'Total Penjualan']],
        body,
        ...pdfTableTheme,
      });
    }

    doc.save('rekap_komprehensif.pdf');
  };

  const handleExportExcel = async () => {
    const formatIndoDate = (isoDate) => {
      const [y, m, d] = (isoDate || '').split('-');
      return y && m && d ? `${d}-${m}-${y}` : isoDate;
    };
    const outletName = user.role === 'superadmin' ? 'Semua Cabang' : (user?.cabang?.namacabang || '-');
    const tanggalLabel = startDate === endDate
      ? formatIndoDate(startDate)
      : `${formatIndoDate(startDate)} s.d. ${formatIndoDate(endDate)}`;

    const rows = [];
    const subtotalRowIndexes = [];
    let no = 1;
    let grandQty = 0;
    let grandTotal = 0;

    const grouped = {};
    detailedDataSorted.forEach((item) => {
      if (!grouped[item.category]) grouped[item.category] = [];
      grouped[item.category].push(item);
    });
    Object.entries(grouped).forEach(([category, items]) => {
      let catQty = 0;
      let catTotal = 0;
      items.forEach((item) => {
        const qty = item.sales?.quantity || 0;
        const total = item.sales?.amount || 0;
        catQty += qty;
        catTotal += total;
        const row = [no++, item.productName, category];
        if (showBranchColumn) row.push(getBranchName(item));
        row.push(qty, total);
        rows.push(row);
      });
      const subtotalRow = showBranchColumn
        ? [`Total Kategori ${category}`, '', '', '', catQty, catTotal]
        : [`Total Kategori ${category}`, '', '', catQty, catTotal];
      rows.push(subtotalRow);
      subtotalRowIndexes.push(rows.length - 1);
      grandQty += catQty;
      grandTotal += catTotal;
    });

    await exportStyledExcel({
      fileName: `Laporan_Rekap_${startDate}_${endDate}.xlsx`,
      sheetName: 'Laporan Penjualan',
      title: 'Laporan Penjualan Produk',
      metaRows: [['Outlet', outletName], ['Tanggal', tanggalLabel]],
      headers: showBranchColumn
        ? ['No', 'Nama Produk', 'Kategori', 'Cabang', 'Terjual', 'Total']
        : ['No', 'Nama Produk', 'Kategori', 'Terjual', 'Total'],
      rows,
      columnWidths: showBranchColumn ? [6, 32, 16, 18, 10, 15] : [8, 40, 18, 10, 15],
      subtotalRowIndexes,
      subtotalLabelSpan: showBranchColumn ? 4 : 2,
      grandTotalRow: { label: 'GRAND TOTAL', values: [grandQty, grandTotal] },
    });
  };

  return (
    <Box p={3}>
      <Paper sx={{ padding: 3 }}>
        <Typography variant="h5">Rekap Komprehensif</Typography>

        {/* Input Tanggal */}
        <Grid container spacing={2} my={2}>
          <Grid item>
            <TextField
              label="Tanggal Mulai"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <TextField
              label="Tanggal Akhir"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {user.role === 'superadmin' && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter Cabang</InputLabel>
                <Select
                  value={selectedCabang}
                  onChange={(e) => setSelectedCabang(e.target.value)}
                  label="Filter Cabang"
                >
                  <MenuItem value="">
                    <em>Semua Cabang</em>
                  </MenuItem>
                  {cabangList?.data?.map((cabang) => (
                    <MenuItem key={cabang.uuid} value={cabang.uuid}>
                      {cabang.namacabang}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        {/* Ringkasan */}
        <Typography variant="h6">Ringkasan</Typography>
        <Box my={2}>
          <Typography>Total Produk: {data.summary.totalProducts}</Typography>
          <Typography>Total Kategori: {data.summary.totalCategories}</Typography>
          <Typography>Total Stok: {data.summary.totalStock}</Typography>
          <Typography>Total Penjualan: Rp {data.summary.totalSalesAmount.toLocaleString()}</Typography>
        </Box>

        {/* Produk Terlaris */}
        <Typography variant="h6">Produk Terlaris</Typography>
        <TableContainer sx={{ mt: 2, maxHeight: 400, overflowY: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Nama Produk</TableCell>
                <TableCell>Kategori</TableCell>
                <TableCell>Harga</TableCell>
                <TableCell>Jumlah Terjual</TableCell>
                <TableCell>Total Penjualan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.summary.topSellingProducts.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell>Rp {getProductPrice(item.name).toLocaleString()}</TableCell>
                  <TableCell>{item.salesQuantity || '0'}</TableCell>
                  <TableCell>Rp {item.salesAmount.toLocaleString() || '0'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Kategori Terlaris */}
        <Typography variant="h6" sx={{ mt: 4 }}>Kategori Terlaris</Typography>
        <TableContainer sx={{ mt: 2, maxHeight: 400, overflowY: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Nama Kategori</TableCell>
                <TableCell>Jumlah Terjual</TableCell>
                <TableCell>Total Penjualan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.summary.topSellingCategories.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.categoryName}</TableCell>
                  <TableCell>{item.salesQuantity}</TableCell>
                  <TableCell>Rp {item.salesAmount.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Performa Cabang (Hanya untuk Superadmin) */}
        {user.role === "superadmin" && data.summary.branchPerformance && (
          <>
            <Typography variant="h6" sx={{ mt: 4 }}>Performa Cabang</Typography>
            <TableContainer sx={{ mt: 2, maxHeight: 400, overflowY: "auto" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Nama Cabang</TableCell>
                    <TableCell>Jumlah Transaksi</TableCell>
                    <TableCell>Total Penjualan</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.summary.branchPerformance.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.branchName}</TableCell>
                      <TableCell>{item.transactionCount}</TableCell>
                      <TableCell>Rp {item.totalSales.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Data Detail Produk */}
        <Typography variant="h6" sx={{ mt: 4 }}>Data Detail Produk</Typography>
        <TableContainer sx={{ mt: 2, maxHeight: 400, overflowY: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Nama Produk</TableCell>
                <TableCell>Kategori</TableCell>
                {showBranchColumn && <TableCell>Cabang</TableCell>}
                <TableCell>Stok Total</TableCell>
                <TableCell>Jumlah Terjual</TableCell>
                <TableCell>Total Penjualan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detailedDataSorted.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.productName}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  {showBranchColumn && <TableCell>{getBranchName(item)}</TableCell>}
                  <TableCell>{item.totalStock}</TableCell>
                  <TableCell>{item.sales.quantity}</TableCell>
                  <TableCell>Rp {item.sales.amount.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Tombol Ekspor */}
        <Button variant="contained" color="primary" onClick={handleExportPDF} sx={{ mt: 3 }}>
          Ekspor ke PDF
        </Button>
        <Button variant="contained" color="success" onClick={handleExportExcel} sx={{ mt: 3, ml: 2 }}>
          Ekspor ke Excel
        </Button>
      </Paper>
    </Box>
  );
};
